import React, { useRef } from "react";
import { CCRichText } from "@pymblecitizencoders/cc-ui/editor";
import { sameRichText } from "../../utils/richText";

/**
 * `CCRichText`, reporting only the changes a PERSON made.
 *
 * ## The problem
 *
 * TipTap does not hand back what it was given. It parses the value into its own
 * schema and re-serialises, so stored markup comes out normalised — tags closed
 * its way, unsupported markup dropped, bare URLs linkified by the `autolink`
 * extension — and an empty value comes out as `<p></p>` because the schema has
 * no concept of nothing. Every one of those differences arrives through
 * `onChange` looking exactly like an edit.
 *
 * In this editor that meant simply OPENING an event marked it as having unsaved
 * changes: Submit blocked, Discard offered, and on the terms-and-conditions
 * field a sign-off invalidated on the next save, because `Approval.*` is
 * material. It was reported twice, once for an event with no notes and once for
 * an event with notes, which are the two halves of the same fault.
 *
 * ## The guard
 *
 * A CHANGE REQUIRES FOCUS. Typing, pasting and pressing a toolbar button all
 * need the editor focused; normalisation and `autolink` do not, because they run
 * from the document being set. So an emission arriving while focus is elsewhere
 * is the editor talking to itself and is dropped.
 *
 * Focus rather than "ignore the first emission", which was the other candidate:
 * that one drops a keystroke when there was nothing to normalise, and if the
 * person types a single character and clicks away it is lost outright.
 *
 * `sameRichText` still runs, so a genuine edit that ends where it started — type
 * a word, delete it — does not count either.
 *
 * The ref rather than state: this is read inside a callback that must not be
 * rebuilt per keystroke, and nothing renders differently for it.
 */

interface GuardedRichTextProps {
	/** The value handed to the editor. Compared against, so pass what you display. */
	value: string;
	/** Fires only for a change a person made, with the editor's own HTML. */
	onChange: (html: string) => void;
	editable?: boolean;
	placeholder?: string;
	className?: string;
	onImageRefused?: (message: string) => void;
}

export const GuardedRichText: React.FC<GuardedRichTextProps> = ({
	value,
	onChange,
	editable = true,
	placeholder,
	className,
	onImageRefused,
}) => {
	const focused = useRef(false);

	return (
		/* React's onFocus/onBlur are focusin/focusout, so they bubble from the
		   contenteditable inside. A plain `focus` listener would not. */
		<div
			className="em-rte-guard"
			onFocus={() => {
				focused.current = true;
			}}
			onBlur={() => {
				focused.current = false;
			}}
		>
			<CCRichText
				className={className}
				value={value}
				editable={editable}
				placeholder={placeholder}
				onChange={(html: string) => {
					if (!focused.current) return;
					if (sameRichText(html, value)) return;
					onChange(html);
				}}
				onImageRefused={onImageRefused}
			/>
		</div>
	);
};
